import type { BasemapOverride, UiMode } from '../types';

export const UI_MODE_STORAGE_KEY = 'beachlab.map.uiMode.v1';
export const LEGACY_THEME_STORAGE_KEY = 'beachlab.map.theme';

export type ChromeMode = Exclude<UiMode, 'auto'>;
export type PairedBasemap = 'gaia' | 'grayscale';
export type ResolvedBasemap = PairedBasemap | BasemapOverride;

export interface ResolvedTheme {
  chromeMode: ChromeMode;
  pairedBasemap: PairedBasemap;
  basemap: ResolvedBasemap;
  override: BasemapOverride | null;
}

export interface ThemeUrlPreference {
  mode: ChromeMode | null;
  override: BasemapOverride | null;
}

export interface InitialThemePreference {
  mode: UiMode;
  override: BasemapOverride | null;
}

interface StorageReader {
  getItem(key: string): string | null;
}

interface StorageWriter extends StorageReader {
  setItem(key: string, value: string): unknown;
}

const overrides: readonly BasemapOverride[] = [
  'satellite',
  'light',
  'dark',
  'bright',
  'orbita',
  'black',
  'white'
];

export function resolveTheme(
  mode: UiMode,
  prefersDark: boolean,
  override: BasemapOverride | null
): ResolvedTheme {
  const chromeMode: ChromeMode = mode === 'auto' ? (prefersDark ? 'night' : 'day') : mode;
  const pairedBasemap: PairedBasemap = chromeMode === 'night' ? 'grayscale' : 'gaia';

  return {
    chromeMode,
    pairedBasemap,
    basemap: override ?? pairedBasemap,
    override
  };
}

export function clearBasemapOverride(): null {
  return null;
}

export function themePreferenceFromUrl(search: string): ThemeUrlPreference {
  const theme = new URLSearchParams(search).get('theme')?.toLowerCase();
  if (theme === 'gaia') return { mode: 'day', override: null };
  if (theme === 'grayscale') return { mode: 'night', override: null };
  if (theme && overrides.includes(theme as BasemapOverride)) {
    return { mode: null, override: theme as BasemapOverride };
  }
  return { mode: null, override: null };
}

export function readStoredUiMode(storage: StorageReader = localStorage): UiMode {
  const stored = storage.getItem(UI_MODE_STORAGE_KEY);
  return stored === 'day' || stored === 'night' || stored === 'auto' ? stored : 'auto';
}

export function readTheme(
  search: string,
  storage: StorageReader = localStorage
): InitialThemePreference {
  const urlPreference = themePreferenceFromUrl(search);
  const storedUiMode = storage.getItem(UI_MODE_STORAGE_KEY);
  const hasStoredUiMode = storedUiMode === 'day' || storedUiMode === 'night' || storedUiMode === 'auto';
  let mode: UiMode = hasStoredUiMode ? storedUiMode : 'auto';
  let legacyOverride: BasemapOverride | null = null;

  if (!hasStoredUiMode) {
    const legacyTheme = storage.getItem(LEGACY_THEME_STORAGE_KEY)?.toLowerCase();
    if (legacyTheme === 'gaia') mode = 'day';
    else if (legacyTheme === 'grayscale') mode = 'night';
    else if (legacyTheme && overrides.includes(legacyTheme as BasemapOverride)) {
      legacyOverride = legacyTheme as BasemapOverride;
    }
  }

  if (urlPreference.mode) return { mode: urlPreference.mode, override: null };
  if (urlPreference.override) return { mode, override: urlPreference.override };
  return { mode, override: legacyOverride };
}

export function persistUiMode(mode: UiMode, storage: StorageWriter = localStorage): void {
  storage.setItem(UI_MODE_STORAGE_KEY, mode);
}
